/* Copyright (c) 2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { logErrorCodeCheckboxes } from "~/constants";
import { AppliedFilterDefinition } from "~/hooks/useAppliedFilters/types";
import {
  getCountBasedDisplayValue,
  removeSelectedValue,
} from "~/hooks/useAppliedFilters/utils";
import { QueryState } from "~/hooks/useQueryState/useQueryState";
import { getHumanReadableLabel } from "~/tools";
import {
  ActionTiming,
  ResourceDetailsActionsQuery,
} from "../../types/ResourceDetailsActionsQuery";
import { actionTypeCheckboxes, resourceStatusCheckboxes } from "./constants";

type DefinitionKey =
  | "resourceStatuses"
  | "actionNames"
  | "actionTypes"
  | "actionLogOutputs";

type ActionsQueryState = ResourceDetailsActionsQuery & QueryState;

type Definition = AppliedFilterDefinition<ActionsQueryState, DefinitionKey>;

const resourceStatusLabelsByValue = resourceStatusCheckboxes.reduce<
  Record<string, string>
>(
  (accumulator, option) => ({
    ...accumulator,
    [option.value]: option.label,
  }),
  {}
);

const actionTypeLabelsByValue = actionTypeCheckboxes.reduce<
  Record<string, string>
>(
  (accumulator, option) => ({
    ...accumulator,
    [option.value]: option.label,
  }),
  {}
);

const actionLogOutputLabelsByValue = logErrorCodeCheckboxes.reduce<
  Record<string, string>
>(
  (accumulator, option) => ({
    ...accumulator,
    [option.value]: option.label,
  }),
  {}
);

type LabeledCountFilterConfig = {
  key: DefinitionKey;
  label: string;
  countLabels: { singular: string; plural: string };
  labelsByValue?: Record<string, string>;
  preserveRawValues?: boolean;
  getValues: (queryState: ActionsQueryState) => string[] | undefined;
  getExcluded: (queryState: ActionsQueryState) => boolean | undefined;
  clear: () => Partial<ActionsQueryState>;
  valueKey: keyof Pick<
    ActionsQueryState,
    "resourceStatuses" | "actionNames" | "actionTypes" | "actionLogOutputs"
  >;
  excludeKey: keyof Pick<
    ActionsQueryState,
    | "excludeResourceStatuses"
    | "excludeActionNames"
    | "excludeActionTypes"
    | "excludeActionLogOutputs"
  >;
};

const getDisplayLabel = (
  value: string,
  labelsByValue: Record<string, string>,
  preserveRawValues: boolean
) =>
  preserveRawValues
    ? value
    : labelsByValue[value] ?? getHumanReadableLabel(value);

const createLabeledCountFilter = ({
  key,
  label,
  countLabels,
  labelsByValue = {},
  preserveRawValues = false,
  getValues,
  getExcluded,
  clear,
  valueKey,
  excludeKey,
}: LabeledCountFilterConfig): Definition => ({
  key,
  label,
  getValues: (queryState) => getValues(queryState) ?? [],
  getValueLabel: (value) =>
    getDisplayLabel(value, labelsByValue, preserveRawValues),
  getValueDisplay: (queryState, values) => {
    if (values.length === 1) {
      const displayLabel = getDisplayLabel(
        values[0],
        labelsByValue,
        preserveRawValues
      );
      return getExcluded(queryState)
        ? `Exclude: ${displayLabel}`
        : displayLabel;
    }

    return getCountBasedDisplayValue(
      values,
      getExcluded(queryState),
      countLabels
    );
  },
  clear: () => clear(),
  removeValue: (queryState, valueToRemove) => {
    const nextValues = removeSelectedValue(
      getValues(queryState),
      valueToRemove
    );
    return {
      [valueKey]: nextValues,
      [excludeKey]: nextValues.length ? queryState[excludeKey] : false,
    };
  },
});

export const createResourceDetailsActionsAppliedFilters = (
  actionTiming: ActionTiming
): Definition[] => {
  const isPastCurrentActions = actionTiming === ActionTiming.pastCurrent;

  const sharedFilters: Definition[] = [
    createLabeledCountFilter({
      key: "actionNames",
      label: "Action Name",
      countLabels: { singular: "Action Name", plural: "Action Names" },
      preserveRawValues: true,
      getValues: (queryState) => queryState.actionNames,
      getExcluded: (queryState) => queryState.excludeActionNames,
      clear: () => ({ actionNames: [], excludeActionNames: false }),
      valueKey: "actionNames",
      excludeKey: "excludeActionNames",
    }),
    createLabeledCountFilter({
      key: "actionTypes",
      label: "Action Type",
      countLabels: { singular: "Action Type", plural: "Action Types" },
      labelsByValue: actionTypeLabelsByValue,
      getValues: (queryState) => queryState.actionTypes,
      getExcluded: (queryState) => queryState.excludeActionTypes,
      clear: () => ({ actionTypes: [], excludeActionTypes: false }),
      valueKey: "actionTypes",
      excludeKey: "excludeActionTypes",
    }),
  ];

  if (!isPastCurrentActions) {
    return sharedFilters;
  }

  return [
    createLabeledCountFilter({
      key: "resourceStatuses",
      label: "Status",
      countLabels: { singular: "Status", plural: "Statuses" },
      labelsByValue: resourceStatusLabelsByValue,
      getValues: (queryState) => queryState.resourceStatuses,
      getExcluded: (queryState) => queryState.excludeResourceStatuses,
      clear: () => ({ resourceStatuses: [], excludeResourceStatuses: false }),
      valueKey: "resourceStatuses",
      excludeKey: "excludeResourceStatuses",
    }),
    ...sharedFilters,
    createLabeledCountFilter({
      key: "actionLogOutputs",
      label: "Log Output",
      countLabels: { singular: "Log Output", plural: "Log Outputs" },
      labelsByValue: actionLogOutputLabelsByValue,
      getValues: (queryState) => queryState.actionLogOutputs,
      getExcluded: (queryState) => queryState.excludeActionLogOutputs,
      clear: () => ({ actionLogOutputs: [], excludeActionLogOutputs: false }),
      valueKey: "actionLogOutputs",
      excludeKey: "excludeActionLogOutputs",
    }),
  ];
};
